import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  GUN_TIPLERI,
  takvimGunSil,
  takvimGunYaz,
  takvimListesi,
  takvimUfku,
  YARIM_GUN_TIPI_ID,
  type TakvimGunu,
  type TakvimUfku,
} from '../api/yonetim';
import { Diyalog, GerekceDiyalogu } from '../bilesenler/Diyalog';
import { Tablo, type Sutun } from '../bilesenler/Tablo';
import { CokSatir, Dugme, HataKutusu, Metin } from '../bilesenler/temel';
import { bugun, gunEkle, tarihMetni } from '../bicim/tarih';
import { yetkiKullan } from '../yetki/yetki';

/**
 * TAKVİM YÖNETİMİ.
 *
 * Dinî bayram tarihleri HESAPLANMAZ, girilir: hicri takvime bağlıdır ve resmî
 * kaynak Diyanet takvimi ile Resmî Gazete'dir.
 *
 * TAKVİM BİTMİŞSE VARDİYA PLANI ÜRETİLEMEZ: gün tipi bilinmeden hafta tatili
 * ve resmî tatil hesaplanamaz, kayıt bulunmayan gün SESSİZCE normal iş günü
 * sayılır. Ufuk şeridi bunu ay sonunda değil önceden haber verir.
 */

const VARSAYILAN_ILERI_GUN = 365;

export function AyarlarTakvim({
  yazabilir,
  yetkilerHazir,
}: {
  yazabilir: boolean;
  yetkilerHazir: boolean;
}) {
  const sorguIstemcisi = useQueryClient();

  /*
   * UFUK UCU DAHA DAR: `PARAMETRE_YONET | SISTEM_YONET | DONEM_KAPAT`.
   * Liste ucu ayrıca `PERSONEL_GORUNTULE` kabul ediyor. Aynı koşula
   * bağlansaydı yalnız `PERSONEL_GORUNTULE` sahibi kullanıcı için 403 üretirdi.
   */
  const ufukGorebilir = yetkiKullan('PARAMETRE_YONET', 'SISTEM_YONET', 'DONEM_KAPAT');

  const [baslangic, baslangicYaz] = useState(() => bugun());
  const [bitis, bitisYaz] = useState(() => gunEkle(bugun(), VARSAYILAN_ILERI_GUN));

  const [duzenlenen, duzenlenenYaz] = useState<TakvimGunu | 'yeni' | null>(null);
  const [silinen, silinenYaz] = useState<TakvimGunu | null>(null);

  const ufuk = useQuery({
    queryKey: ['yonetim', 'takvim-ufuk'],
    queryFn: () => takvimUfku(),
    enabled: yetkilerHazir && ufukGorebilir,
  });

  const gunler = useQuery({
    queryKey: ['yonetim', 'takvim', { baslangic, bitis }],
    queryFn: () => takvimListesi(baslangic, bitis),
    enabled: yetkilerHazir,
  });

  const sil = useMutation({
    mutationFn: (girdi: { tarih: string; gerekce: string }) =>
      takvimGunSil(girdi.tarih, girdi.gerekce),
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['yonetim', 'takvim'] });
      await sorguIstemcisi.invalidateQueries({ queryKey: ['yonetim', 'takvim-ufuk'] });
      silinenYaz(null);
    },
  });

  const sutunlar: Sutun<TakvimGunu>[] = [
    { baslik: 'Tarih', hucre: (g) => tarihMetni(g.tarih), daralt: true },
    {
      baslik: 'Gün',
      hucre: (g) => (
        <span>
          <span className="block font-medium text-slate-900">{g.ad}</span>
          <span className="block text-xs text-slate-500">
            {g.gunTipiAd}
            {g.yarimGunBaslangic !== null && ` · ${g.yarimGunBaslangic}'ten itibaren`}
          </span>
        </span>
      ),
    },
    {
      baslik: 'Dayanak',
      hucre: (g) => (
        <span className="text-xs">
          {g.dayanakTipi}
          {g.dayanakMadde !== null && ` · ${g.dayanakMadde}`}
        </span>
      ),
    },
    {
      baslik: 'Durum',
      hucre: (g) =>
        g.dogrulanmasiGerekiyorMu ? (
          /*
            HİCRİ TAKVİME BAĞLI GÜNLER. Seed'deki tarih bir TAHMİNDİR;
            doğrulanmadan o dönem kilitlenmemeli. Rozet olmasaydı tahmin ile
            kesinleşmiş tarih listede ayırt edilemezdi.
          */
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            Doğrulanmalı
          </span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        ),
      daralt: true,
    },
    {
      baslik: '',
      hucre: (g) =>
        yazabilir ? (
          <span className="flex justify-end gap-1 whitespace-nowrap">
            <button
              type="button"
              onClick={() => duzenlenenYaz(g)}
              className="rounded px-2 py-1 text-xs text-kurum-700 hover:bg-kurum-50"
            >
              Düzenle
            </button>
            <button
              type="button"
              onClick={() => silinenYaz(g)}
              className="rounded px-2 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Sil
            </button>
          </span>
        ) : null,
      hizala: 'sag',
      daralt: true,
    },
  ];

  return (
    <div className="space-y-4">
      {ufukGorebilir && (
        <>
          <HataKutusu hata={ufuk.error} />
          {ufuk.data && <UfukSeridi veri={ufuk.data} />}
        </>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap gap-3">
          <Metin etiket="Başlangıç" tur="date" deger={baslangic} degisti={baslangicYaz} />
          <Metin etiket="Bitiş" tur="date" deger={bitis} degisti={bitisYaz} />
        </div>

        {yazabilir && <Dugme tikla={() => duzenlenenYaz('yeni')} cocuk="Gün ekle" />}
      </div>

      <HataKutusu hata={gunler.error} />

      <Tablo
        sutunlar={sutunlar}
        satirlar={gunler.data ?? []}
        anahtar={(g) => g.tarih}
        bekliyor={gunler.isPending}
        bosMetin="Bu aralıkta tanımlı özel gün yok — tümü normal iş günü sayılır."
      />

      {duzenlenen !== null && (
        <TakvimGunDiyalogu
          mevcut={duzenlenen === 'yeni' ? null : duzenlenen}
          kapat={() => duzenlenenYaz(null)}
        />
      )}

      {silinen !== null && (
        <GerekceDiyalogu
          baslik={`${silinen.ad} kaydını sil`}
          aciklama={`${tarihMetni(silinen.tarih)} · Kaydın VARLIĞI tatil anlamına geldiği için pasife alma seçeneği yoktur; kayıt tamamen silinir ve gün normal iş günü olur.`}
          onayEtiketi="Sil"
          onayTuru="tehlike"
          enCokKarakter={500}
          bekliyor={sil.isPending}
          hata={sil.error}
          kapat={() => silinenYaz(null)}
          onayla={(gerekce) => sil.mutate({ tarih: silinen.tarih, gerekce })}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function UfukSeridi({ veri }: { veri: TakvimUfku }) {
  const bicim: Record<string, string> = {
    normal: 'border-slate-200 bg-white',
    uyari: 'border-amber-200 bg-amber-50',
    engel: 'border-red-200 bg-red-50',
  };

  return (
    <section className={`rounded-lg border p-4 ${bicim[veri.durum] ?? bicim.normal}`}>
      <p className="text-sm font-medium text-slate-900">{veri.mesaj}</p>
      <p className="mt-1 text-xs text-slate-600">
        {veri.sonTarih === null
          ? 'Takvimde hiç kayıt yok.'
          : `Son tanımlı gün ${tarihMetni(veri.sonTarih)} · ${veri.kalanGun} gün kaldı`}
        {veri.dogrulanmamisGun > 0 && ` · ${veri.dogrulanmamisGun} gün doğrulanmalı`}
      </p>
      {veri.durum === 'engel' && (
        <p className="mt-2 text-xs font-medium text-red-800">
          Plan üretimi durur: kayıt bulunmayan gün sessizce normal iş günü sayılır.
        </p>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */

/**
 * TAKVİM GÜNÜ EKLE/GÜNCELLE.
 *
 * YARIM GÜN KISITI VERİTABANINDA ÇİFT YÖNLÜ:
 *   gun_tipi_id  = 30  →  yarim_gun_baslangic ZORUNLU
 *   gun_tipi_id <> 30  →  yarim_gun_baslangic NULL OLMALI
 *
 * Formda karşılanmazsa CHECK kısıtı 422 döndürür. Panelde de uygulanıyor:
 * kullanıcı saati doldurup tipi değiştirdiğinde alan gizlenir ve değer
 * GÖNDERİLMEZ — gizlenip yine gönderilseydi ikinci koşul ihlal edilirdi.
 */
function TakvimGunDiyalogu({ mevcut, kapat }: { mevcut: TakvimGunu | null; kapat: () => void }) {
  const sorguIstemcisi = useQueryClient();

  const [tarih, tarihYaz] = useState(mevcut?.tarih ?? bugun());
  const [gunTipiId, gunTipiIdYaz] = useState<number>(mevcut?.gunTipiId ?? GUN_TIPLERI[1].id);
  const [ad, adYaz] = useState(mevcut?.ad ?? '');
  const [yarimGunBaslangic, yarimGunBaslangicYaz] = useState(mevcut?.yarimGunBaslangic ?? '13:00');
  const [dayanakTipi, dayanakTipiYaz] = useState(mevcut?.dayanakTipi ?? 'kanun');
  const [dayanakMadde, dayanakMaddeYaz] = useState(mevcut?.dayanakMadde ?? '');
  const [aciklama, aciklamaYaz] = useState(mevcut?.aciklama ?? '');
  const [gerekce, gerekceYaz] = useState('');

  const yarimGun = gunTipiId === YARIM_GUN_TIPI_ID;

  const yaz = useMutation({
    mutationFn: () =>
      takvimGunYaz({
        tarih,
        gunTipiId,
        ad: ad.trim(),
        // Yarım gün DEĞİLSE alan hiç gönderilmez; DB `NULL` bekliyor.
        yarimGunBaslangic: yarimGun ? yarimGunBaslangic : undefined,
        dayanakTipi: dayanakTipi.trim(),
        dayanakMadde: dayanakMadde.trim() === '' ? undefined : dayanakMadde.trim(),
        aciklama: aciklama.trim() === '' ? undefined : aciklama.trim(),
        gerekce: gerekce.trim(),
      }),
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['yonetim', 'takvim'] });
      await sorguIstemcisi.invalidateQueries({ queryKey: ['yonetim', 'takvim-ufuk'] });
      kapat();
    },
  });

  const gonderilebilir =
    tarih !== '' &&
    ad.trim() !== '' &&
    dayanakTipi.trim() !== '' &&
    gerekce.trim() !== '' &&
    (!yarimGun || yarimGunBaslangic !== '');

  return (
    <Diyalog
      baslik={mevcut === null ? 'Takvim günü ekle' : `${mevcut.ad} kaydını düzenle`}
      aciklama="Dinî bayram tarihleri hesaplanmaz, girilir: hicri takvime bağlıdır ve resmî kaynak Diyanet takvimi ile Resmî Gazete'dir."
      kapat={kapat}
      genis
      cocuk={
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Metin etiket="Tarih" tur="date" deger={tarih} degisti={tarihYaz} />

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Gün tipi</span>
              <select
                value={gunTipiId}
                onChange={(olay) => gunTipiIdYaz(Number(olay.target.value))}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
              >
                {GUN_TIPLERI.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.ad}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <Metin etiket="Gün adı" deger={ad} degisti={adYaz} />

          {/*
            SAAT ALANI YALNIZ YARIM GÜNDE GÖRÜNÜR ve yalnız o zaman gönderilir.
            2429/2 gereği yarım gün tatiller 13:00'ten itibaren başlar.
          */}
          {yarimGun && (
            <label className="block max-w-xs">
              <span className="mb-1 block text-sm font-medium text-slate-700">
                Yarım gün başlangıcı
              </span>
              <input
                type="time"
                value={yarimGunBaslangic}
                onChange={(olay) => yarimGunBaslangicYaz(olay.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
              />
              <span className="mt-1 block text-xs text-slate-500">
                Zorunlu — 2429/2 gereği 13:00.
              </span>
            </label>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Metin etiket="Dayanak tipi" deger={dayanakTipi} degisti={dayanakTipiYaz} />
            <Metin
              etiket="Dayanak maddesi (isteğe bağlı)"
              deger={dayanakMadde}
              degisti={dayanakMaddeYaz}
            />
          </div>

          <CokSatir
            etiket="Açıklama (isteğe bağlı)"
            deger={aciklama}
            degisti={aciklamaYaz}
            satir={2}
            ipucu="Hicri takvime bağlı tarihlerde “DOĞRULANMALI” notu bırakın."
          />

          <CokSatir
            etiket="Gerekçe"
            deger={gerekce}
            degisti={gerekceYaz}
            satir={2}
            ipucu="Zorunlu — denetim izine yazılır."
          />

          <HataKutusu hata={yaz.error} />

          {yaz.data && yaz.data.uyarilar.length > 0 && (
            <ul className="list-disc space-y-0.5 rounded-md border border-amber-200 bg-amber-50 p-3 pl-8 text-xs text-amber-900">
              {yaz.data.uyarilar.map((u) => (
                <li key={u}>{u}</li>
              ))}
            </ul>
          )}

          <div className="flex justify-end gap-2">
            <Dugme tur="ikincil" tikla={kapat} pasif={yaz.isPending} cocuk="Vazgeç" />
            <Dugme
              tikla={() => yaz.mutate()}
              bekliyor={yaz.isPending}
              pasif={!gonderilebilir}
              cocuk="Kaydet"
            />
          </div>
        </div>
      }
    />
  );
}
